import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

export interface UserProfile {
	firstName: string,
	lastName: string,
	email: string,
	avatarUrl?: string | null,
}

export type UserRole = 'guest' | 'user' | 'admin'

export type UserStatus = 'guest' | 'authenticated' | 'blocked'

interface UserState {
	profile: UserProfile | null,
	isAuthenticated: boolean,
	role: UserRole,
	status: UserStatus,
}

const initialState: UserState = {
	profile: null,
	isAuthenticated: false,
	role: 'guest',
	status: 'guest',
}

const userSlice = createSlice({
	name: 'user',
	initialState,
	selectors: {
		getUserProfile: (state) => state.profile,
		getIsAuthenticated: (state) => state.isAuthenticated,
		getUserRole: (state) => state.role,
		getUserStatus: (state) => state.status,
		getUserAvatarUrl: (state) => state.profile?.avatarUrl,
	},
	reducers: {
		setUserProfile: (state, action: PayloadAction<UserProfile>) => {
			state.profile = action.payload
		},
		updateUserProfile: (state, action: PayloadAction<Partial<UserProfile>>) => {
			if (!state.profile) {
				state.profile = {
					firstName: '',
					lastName: '',
					email: '',
					...action.payload,
				}
				return;
			}

			state.profile = {
				...state.profile,
				...action.payload,
			}
		},
		setAuthenticated: (state, action: PayloadAction<boolean>) => {
			state.isAuthenticated = action.payload
			state.status = action.payload ? 'authenticated' : 'guest'
			if (action.payload && state.role === 'guest') {
				state.role = 'user'
			}
		},
		setUserRole: (state, action: PayloadAction<UserRole>) => {
			state.role = action.payload
		},
		setUserStatus: (state, action: PayloadAction<UserStatus>) => {
			state.status = action.payload
			if (action.payload === 'authenticated') {
				state.isAuthenticated = true
			}
			if (action.payload === 'guest') {
				state.isAuthenticated = false
			}
			if (action.payload === 'blocked') {
				state.isAuthenticated = false
			}
		},
		setGuestProfile: (state, action: PayloadAction<UserProfile | null>) => {
			state.profile = action.payload
			state.role = 'guest'
			state.status = 'guest'
			state.isAuthenticated = false
		},
		logoutUser: (state) => {
			state.profile = null
			state.isAuthenticated = false
			state.role = 'guest'
			state.status = 'guest'
		},
	},
})

export const {
	setUserProfile,
	updateUserProfile,
	setAuthenticated,
	setUserRole,
	setUserStatus,
	setGuestProfile,
	logoutUser,
} = userSlice.actions

export const {
	getUserProfile,
	getIsAuthenticated,
	getUserRole,
	getUserStatus,
	getUserAvatarUrl,
} = userSlice.selectors

export default userSlice
